import React, { useEffect, useState } from 'react';
import { getHistory } from '@/utils/UserHistoryManager';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { History, Shield, UserCheck, AlertTriangle } from 'lucide-react';

/**
 * UserHistoryViewer
 * Displays an audit trail for a given user.
 * Props:
 *   - userId: Discord ID of the user whose history we want to view.
 *   - userName: Friendly name to display in the header.
 */
const UserHistoryViewer = ({ userId, userName }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) return;
        const fetchHistory = async () => {
            try {
                const entries = await getHistory(userId);
                // Sort newest first
                const sorted = entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                setHistory(sorted);
            } catch (e) {
                console.error('Failed to load user history', e);
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, [userId]);

    if (loading) {
        return <p className="text-gray-400">Carregando histórico...</p>;
    }

    if (!history.length) {
        return <p className="text-gray-400">Nenhum registro encontrado.</p>;
    }

    return (
        <ScrollArea className="h-[60vh] w-full">
            <ul className="space-y-4 p-4">
                {history.map((entry) => (
                    <li key={entry.id} className="border-b border-white/10 pb-2">
                        <div className="flex items-center space-x-2 mb-1">
                            <History className="h-4 w-4 text-purple-400" />
                            <span className="text-sm font-medium text-white">
                                {new Date(entry.timestamp).toLocaleString('pt-BR')}
                            </span>
                            {entry.isAdminChange && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                    Admin
                                </Badge>
                            )}
                        </div>
                        <div className="text-sm text-gray-300">
                            <strong>Tipo:</strong> {entry.changeType}
                        </div>
                        <div className="text-sm text-gray-300">
                            <strong>Valor antigo:</strong>{' '}
                            <span className="font-mono">{JSON.stringify(entry.oldValue)}</span>
                        </div>
                        <div className="text-sm text-gray-300">
                            <strong>Valor novo:</strong>{' '}
                            <span className="font-mono">{JSON.stringify(entry.newValue)}</span>
                        </div>
                    </li>
                ))}
            </ul>
        </ScrollArea>
    );
};

export default UserHistoryViewer;
